"use client";

import { Loader2, Search } from "lucide-react";
import { useRouter } from "next/navigation";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { projectDetailPath } from "@/lib/aicheck/routes";
import { authService } from "@/services/auth";

type SearchField = "all" | "id" | "tour_code" | "name";

const FIELD_LABELS: Record<SearchField, string> = {
  all: "総合",
  id: "旅程ID",
  tour_code: "ツアー番号",
  name: "ツアー名",
};

type SuggestionMatch =
  | "id"
  | "tour_code"
  | "title"
  | "customer"
  | "agency"
  | "history"
  | "backend";

type CheckLanguage = "ja" | "en";
type LanguageChoice = "auto" | CheckLanguage;

interface ProjectSuggestion {
  projectId: number;
  title: string;
  tourCode: number | null;
  travelStartDate: string | null;
  travelEndDate: string | null;
  status: string | null;
  matchedBy: SuggestionMatch;
  checkedLanguages: CheckLanguage[];
  checkedAt: string | null;
  sourceKind?: "crm" | "external";
  projectKey?: string;
  /** 外部案件の公開ID（`ext_...`）。CRM 案件では null。 */
  externalRef?: string | null;
  sourceLabel?: string | null;
}

const MATCHED_LABELS: Record<SuggestionMatch, string> = {
  id: "旅程ID一致",
  tour_code: "ツアー番号一致",
  title: "名称一致",
  customer: "顧客名一致",
  agency: "代理店一致",
  history: "チェック履歴一致",
  backend: "備考等に一致",
};

export function ProjectSearchBox({
  initialValue = "",
}: {
  initialValue?: string;
}) {
  const router = useRouter();
  const [query, setQuery] = useState(initialValue);
  const [field, setField] = useState<SearchField>("all");
  const [language, setLanguage] = useState<LanguageChoice>("auto");
  const [suggestions, setSuggestions] = useState<ProjectSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (initialValue) setQuery(initialValue);
  }, [initialValue]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const runSearch = useCallback((value: string, searchField: SearchField) => {
    abortRef.current?.abort();
    if (!value.trim()) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    void (async () => {
      try {
        const token = await authService.getIdToken();
        if (!token) throw new Error("未認証です。再度サインインしてください。");
        const res = await fetch(
          apiUrl(
            `/api/aicheck/crm/projects/search?q=${encodeURIComponent(value.trim())}&field=${searchField}`,
          ),
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          },
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          suggestions?: ProjectSuggestion[];
        };
        if (controller.signal.aborted) return;
        setSuggestions(data.suggestions ?? []);
        setHighlighted(data.suggestions?.length ? 0 : -1);
        setOpen(true);
      } catch {
        // aborted or network error — keep previous suggestions
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runSearch(value, field), 300);
    },
    [field, runSearch],
  );

  const handleFieldChange = useCallback(
    (next: SearchField) => {
      setField(next);
      if (query.trim()) runSearch(query, next);
    },
    [query, runSearch],
  );

  const pick = useCallback(
    (suggestion: ProjectSuggestion) => {
      setOpen(false);
      setQuery(String(suggestion.projectId));
      // 外部データの案件は CRM から取得できない（別データを取ってしまう）ため、
      // 保存済みスナップショットを持つ案件ページへ誘導する。
      if (suggestion.sourceKind === "external" && suggestion.projectKey) {
        router.push(projectDetailPath(suggestion.projectKey));
        return;
      }
      // Explicit choice wins; auto prefers a language that already has a
      // saved result (ja first), and otherwise lets the page decide.
      const lang =
        language !== "auto"
          ? language
          : (suggestion.checkedLanguages[0] ?? null);
      router.push(
        projectDetailPath(suggestion.projectId, lang ? { lang } : undefined),
      );
    },
    [router, language],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!open && suggestions.length) setOpen(true);
        setHighlighted((index) =>
          suggestions.length ? (index + 1) % suggestions.length : -1,
        );
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlighted((index) =>
          suggestions.length
            ? (index - 1 + suggestions.length) % suggestions.length
            : -1,
        );
        return;
      }
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        if (open && highlighted >= 0 && suggestions[highlighted]) {
          pick(suggestions[highlighted]);
          return;
        }
        // Fallback: numeric input loads the itinerary id directly.
        const id = Number(query.trim());
        if (Number.isInteger(id) && id > 0 && field !== "tour_code") {
          setOpen(false);
          router.push(
            projectDetailPath(
              id,
              language !== "auto" ? { lang: language } : undefined,
            ),
          );
        }
      }
    },
    [open, highlighted, suggestions, pick, query, field, language, router],
  );

  return (
    <div ref={containerRef} className="relative flex items-center gap-1.5">
      <select
        value={field}
        onChange={(e) => handleFieldChange(e.target.value as SearchField)}
        aria-label="検索対象"
        className="h-7 rounded border border-zinc-700 bg-zinc-800 px-1.5 text-xs text-zinc-300 outline-none focus:border-zinc-500"
      >
        {(Object.keys(FIELD_LABELS) as SearchField[]).map((key) => (
          <option key={key} value={key}>
            {FIELD_LABELS[key]}
          </option>
        ))}
      </select>
      {/* Output language for the check result to open/run */}
      <fieldset
        className="flex h-7 items-center overflow-hidden rounded border border-zinc-700"
        aria-label="チェック言語"
      >
        {(["auto", "ja", "en"] as LanguageChoice[]).map((choice) => (
          <button
            key={choice}
            type="button"
            onClick={() => setLanguage(choice)}
            title={
              choice === "auto"
                ? "チェック済みの言語を優先"
                : `${choice.toUpperCase()} で開く / 実行`
            }
            className={`h-full px-2 text-[11px] font-medium transition-colors ${
              language === choice
                ? "bg-zinc-600 text-white"
                : "bg-zinc-800 text-zinc-400 hover:text-white"
            }`}
          >
            {choice === "auto" ? "自動" : choice.toUpperCase()}
          </button>
        ))}
      </fieldset>
      <div className="relative">
        <Search
          size={12}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            if (suggestions.length) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            field === "all"
              ? "ID / ツアー番号 / ツアー名"
              : `${FIELD_LABELS[field]}で検索`
          }
          aria-label="案件検索"
          className="h-7 w-56 rounded border border-zinc-700 bg-zinc-800 pl-7 pr-6 text-xs text-white outline-none focus:border-zinc-500 placeholder:text-zinc-600"
        />
        {loading && (
          <Loader2
            size={12}
            className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-zinc-500"
          />
        )}

        {open && suggestions.length > 0 && (
          <div className="absolute right-0 top-[calc(100%+0.4rem)] z-50 w-[26rem] overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-xl">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.projectKey ?? suggestion.projectId}
                type="button"
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => pick(suggestion)}
                className={`flex w-full flex-col gap-0.5 border-b border-zinc-100 px-3 py-2 text-left last:border-b-0 ${
                  index === highlighted ? "bg-blue-50" : "bg-white"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-800">
                    {suggestion.title}
                  </span>
                  {suggestion.sourceKind === "external" && (
                    <span
                      className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700"
                      title={suggestion.sourceLabel ?? "外部データ"}
                    >
                      外部データ
                    </span>
                  )}
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      suggestion.matchedBy === "backend"
                        ? "bg-zinc-100 text-zinc-500"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {MATCHED_LABELS[suggestion.matchedBy]}
                  </span>
                  {suggestion.checkedLanguages.map((lang) => (
                    <span
                      key={lang}
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        language === lang
                          ? "bg-indigo-600 text-white"
                          : "bg-indigo-50 text-indigo-700"
                      }`}
                      title={
                        suggestion.checkedAt
                          ? `${lang.toUpperCase()} チェック済み (最終: ${new Date(suggestion.checkedAt).toLocaleString("ja-JP")})`
                          : `${lang.toUpperCase()} チェック済み`
                      }
                    >
                      ✓ {lang}
                    </span>
                  ))}
                  {language !== "auto" &&
                    !suggestion.checkedLanguages.includes(language) && (
                      <span
                        className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700"
                        title={`${language.toUpperCase()} の保存結果はありません。選択すると確認の上で新規チェックを実行します`}
                      >
                        {language} 未チェック
                      </span>
                    )}
                </span>
                <span className="flex items-center gap-3 text-[11px] text-zinc-500">
                  <span>
                    {suggestion.externalRef
                      ? `公開ID: ${suggestion.externalRef}`
                      : `旅程ID: ${suggestion.projectId}`}
                  </span>
                  {suggestion.sourceKind === "external" &&
                    suggestion.sourceLabel && (
                      <span className="min-w-0 max-w-[14rem] truncate">
                        {suggestion.sourceLabel}
                      </span>
                    )}
                  {suggestion.tourCode != null && (
                    <span>ツアー番号: {suggestion.tourCode}</span>
                  )}
                  {suggestion.travelStartDate && (
                    <span>
                      {suggestion.travelStartDate}
                      {suggestion.travelEndDate
                        ? ` 〜 ${suggestion.travelEndDate}`
                        : ""}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}

        {open && !loading && suggestions.length === 0 && query.trim() && (
          <div className="absolute right-0 top-[calc(100%+0.4rem)] z-50 w-64 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs text-zinc-500 shadow-xl">
            該当する案件が見つかりません
          </div>
        )}
      </div>
    </div>
  );
}
